import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, MapPin, Clock, Send, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Overline, EASE } from "@/lib/Reveal";
import { COMPANY, SERVICES } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
    name: "",
    phone: "",
    email: "",
    service: "",
    property_address: "",
    contact_method: "phone",
    details: "",
};

const inputCls =
    "w-full rounded-xl border border-[#E5E5E0] bg-white px-4 py-3.5 text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-[#E85A1D]/60 focus:border-[#E85A1D] transition-[border-color,box-shadow] duration-200";

export default function QuoteContact() {
    const [form, setForm] = useState(EMPTY);
    const [errors, setErrors] = useState({});
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    useEffect(() => {
        const onPrefill = (e) => setForm((f) => ({ ...f, service: e.detail }));
        window.addEventListener("prefill-service", onPrefill);
        return () => window.removeEventListener("prefill-service", onPrefill);
    }, []);

    const set = (k) => (e) => {
        setForm({ ...form, [k]: e.target.value });
        if (errors[k]) setErrors({ ...errors, [k]: null });
    };

    const validate = () => {
        const errs = {};
        if (form.name.trim().length < 2) errs.name = "Please enter your name.";
        if (form.phone.trim().length < 7) errs.phone = "Please enter a valid phone number.";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errs.email = "Please enter a valid email address.";
        if (!form.service) errs.service = "Please select a service.";
        return errs;
    };

    const submit = async (e) => {
        e.preventDefault();
        const errs = validate();
        if (Object.keys(errs).length) {
            setErrors(errs);
            return;
        }
        setSending(true);
        try {
            const res = await fetch(`${API}/enquiries`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(form),
            });
            if (!res.ok) throw new Error("Request failed");
            setSent(true);
            setForm(EMPTY);
            toast.success("Enquiry sent — we will be in touch shortly.");
        } catch {
            toast.error("Something went wrong. Please call us on 07718 580408 instead.");
        } finally {
            setSending(false);
        }
    };

    return (
        <section id="contact" data-testid="contact-section" className="py-20 lg:py-28 bg-[#141414] noise-overlay relative">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <div className="max-w-2xl mb-14">
                    <Reveal>
                        <Overline light>Get a Free Quote</Overline>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white mt-4 leading-[1.05]">
                            Tell us about your project
                        </h2>
                        <p className="mt-5 text-base text-white/60 leading-relaxed">
                            Fill in the form and we will get back to you with a free, no-obligation quote. Prefer to
                            talk? Call us directly — we are happy to help.
                        </p>
                    </Reveal>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    <Reveal className="lg:col-span-7">
                        <div className="bg-white rounded-2xl p-7 sm:p-10 shadow-[0_24px_70px_rgba(0,0,0,0.35)]">
                            <AnimatePresence mode="wait">
                                {sent ? (
                                    <motion.div
                                        key="success"
                                        data-testid="quote-success-message"
                                        initial={{ opacity: 0, scale: 0.96 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ duration: 0.5, ease: EASE }}
                                        className="text-center py-14"
                                    >
                                        <div className="w-20 h-20 rounded-full bg-[#FFF1EB] flex items-center justify-center mx-auto">
                                            <CheckCircle2 className="w-10 h-10 text-[#E85A1D]" />
                                        </div>
                                        <h3 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-neutral-900 mt-7">
                                            Thank you — enquiry received
                                        </h3>
                                        <p className="mt-4 text-sm sm:text-base text-neutral-600 leading-relaxed max-w-md mx-auto">
                                            We have received your request and will be in touch shortly using your
                                            preferred contact method. For anything urgent, call{" "}
                                            <a href={COMPANY.phoneHref} className="font-bold text-[#E85A1D]">
                                                {COMPANY.phoneDisplay}
                                            </a>
                                            .
                                        </p>
                                        <button
                                            onClick={() => setSent(false)}
                                            className="mt-8 border-2 border-neutral-900 text-neutral-900 font-bold px-7 py-3 rounded-full text-sm hover:bg-neutral-900 hover:text-white transition-colors duration-200"
                                        >
                                            Send Another Enquiry
                                        </button>
                                    </motion.div>
                                ) : (
                                    <motion.form
                                        key="form"
                                        data-testid="quote-form"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0, y: -12 }}
                                        onSubmit={submit}
                                        noValidate
                                        className="grid grid-cols-1 sm:grid-cols-2 gap-5"
                                    >
                                        <div>
                                            <label htmlFor="quote-name" className="block text-xs font-bold uppercase tracking-wider text-neutral-500 mb-2">
                                                Name *
                                            </label>
                                            <input
                                                id="quote-name"
                                                data-testid="quote-input-name"
                                                type="text"
                                                value={form.name}
                                                onChange={set("name")}
                                                placeholder="Your full name"
                                                className={inputCls}
                                            />
                                            {errors.name && <p className="mt-1.5 text-xs font-semibold text-red-600">{errors.name}</p>}
                                        </div>
                                        <div>
                                            <label htmlFor="quote-phone" className="block text-xs font-bold uppercase tracking-wider text-neutral-500 mb-2">
                                                Phone Number *
                                            </label>
                                            <input
                                                id="quote-phone"
                                                data-testid="quote-input-phone"
                                                type="tel"
                                                value={form.phone}
                                                onChange={set("phone")}
                                                placeholder="07xxx xxxxxx"
                                                className={inputCls}
                                            />
                                            {errors.phone && <p className="mt-1.5 text-xs font-semibold text-red-600">{errors.phone}</p>}
                                        </div>
                                        <div>
                                            <label htmlFor="quote-email" className="block text-xs font-bold uppercase tracking-wider text-neutral-500 mb-2">
                                                Email *
                                            </label>
                                            <input
                                                id="quote-email"
                                                data-testid="quote-input-email"
                                                type="email"
                                                value={form.email}
                                                onChange={set("email")}
                                                placeholder="you@example.com"
                                                className={inputCls}
                                            />
                                            {errors.email && <p className="mt-1.5 text-xs font-semibold text-red-600">{errors.email}</p>}
                                        </div>
                                        <div>
                                            <label htmlFor="quote-service" className="block text-xs font-bold uppercase tracking-wider text-neutral-500 mb-2">
                                                Service Required *
                                            </label>
                                            <select
                                                id="quote-service"
                                                data-testid="quote-select-service"
                                                value={form.service}
                                                onChange={set("service")}
                                                className={`${inputCls} ${!form.service ? "text-neutral-400" : ""}`}
                                            >
                                                <option value="" disabled>
                                                    Select a service
                                                </option>
                                                {SERVICES.map((s) => (
                                                    <option key={s.id} value={s.title}>
                                                        {s.title}
                                                    </option>
                                                ))}
                                                <option value="Multiple Services">Multiple Services</option>
                                                <option value="Other">Other / Not Sure</option>
                                            </select>
                                            {errors.service && <p className="mt-1.5 text-xs font-semibold text-red-600">{errors.service}</p>}
                                        </div>
                                        <div className="sm:col-span-2">
                                            <label htmlFor="quote-address" className="block text-xs font-bold uppercase tracking-wider text-neutral-500 mb-2">
                                                Property Address
                                            </label>
                                            <input
                                                id="quote-address"
                                                data-testid="quote-input-address"
                                                type="text"
                                                value={form.property_address}
                                                onChange={set("property_address")}
                                                placeholder="Street, town, postcode"
                                                className={inputCls}
                                            />
                                        </div>
                                        <fieldset className="sm:col-span-2">
                                            <legend className="block text-xs font-bold uppercase tracking-wider text-neutral-500 mb-2">
                                                Preferred Contact Method
                                            </legend>
                                            <div className="flex gap-3">
                                                {[
                                                    { v: "phone", label: "Phone", testid: "quote-radio-contact-method-phone" },
                                                    { v: "email", label: "Email", testid: "quote-radio-contact-method-email" },
                                                ].map((o) => (
                                                    <label
                                                        key={o.v}
                                                        className={`flex-1 flex items-center justify-center gap-2 rounded-xl border-2 px-4 py-3 text-sm font-bold cursor-pointer transition-[border-color,background-color,color] duration-200 ${
                                                            form.contact_method === o.v
                                                                ? "border-[#E85A1D] bg-[#FFF1EB] text-[#E85A1D]"
                                                                : "border-[#E5E5E0] text-neutral-500 hover:border-neutral-400"
                                                        }`}
                                                    >
                                                        <input
                                                            data-testid={o.testid}
                                                            type="radio"
                                                            name="contact_method"
                                                            value={o.v}
                                                            checked={form.contact_method === o.v}
                                                            onChange={set("contact_method")}
                                                            className="sr-only"
                                                        />
                                                        {o.label}
                                                    </label>
                                                ))}
                                            </div>
                                        </fieldset>
                                        <div className="sm:col-span-2">
                                            <label htmlFor="quote-details" className="block text-xs font-bold uppercase tracking-wider text-neutral-500 mb-2">
                                                Project Details
                                            </label>
                                            <textarea
                                                id="quote-details"
                                                data-testid="quote-textarea-details"
                                                rows={4}
                                                value={form.details}
                                                onChange={set("details")}
                                                placeholder="Tell us what you need — the more detail, the more accurate your quote."
                                                className={`${inputCls} resize-none`}
                                            />
                                        </div>
                                        <div className="sm:col-span-2">
                                            <button
                                                data-testid="quote-submit-button"
                                                type="submit"
                                                disabled={sending}
                                                className="group w-full flex items-center justify-center gap-3 bg-[#E85A1D] hover:bg-[#D24B10] disabled:opacity-70 text-white font-bold px-8 py-4.5 py-4 rounded-full text-base transition-colors duration-200"
                                            >
                                                {sending ? (
                                                    <>
                                                        <Loader2 className="w-5 h-5 animate-spin" />
                                                        Sending…
                                                    </>
                                                ) : (
                                                    <>
                                                        Request a Free Quote
                                                        <Send className="w-5 h-5 transition-transform duration-200 group-hover:translate-x-1" />
                                                    </>
                                                )}
                                            </button>
                                            <p className="mt-3 text-center text-xs text-neutral-400">
                                                No obligation. We usually respond the same day.
                                            </p>
                                        </div>
                                    </motion.form>
                                )}
                            </AnimatePresence>
                        </div>
                    </Reveal>

                    <div className="lg:col-span-5 flex flex-col gap-6">
                        <Reveal delay={0.1}>
                            <div className="bg-white/[0.05] border border-white/10 backdrop-blur-sm rounded-2xl p-7 sm:p-8">
                                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {[
                                        { label: "Call us directly", display: COMPANY.phoneDisplay, href: COMPANY.phoneHref, testid: "contact-click-to-call-button" },
                                        { label: "Alternative line", display: COMPANY.phone2Display, href: COMPANY.phone2Href, testid: "contact-click-to-call-button-2" },
                                    ].map((p) => (
                                        <a
                                            key={p.href}
                                            data-testid={p.testid}
                                            href={p.href}
                                            className="group flex items-center gap-4 rounded-xl bg-[#E85A1D] hover:bg-[#D24B10] transition-colors duration-200 p-4"
                                        >
                                            <span className="w-11 h-11 rounded-full bg-white/15 flex items-center justify-center shrink-0">
                                                <Phone className="w-5 h-5 text-white" />
                                            </span>
                                            <span>
                                                <span className="block text-[10px] font-bold uppercase tracking-wider text-white/75">
                                                    {p.label}
                                                </span>
                                                <span className="block font-display text-lg font-extrabold text-white tracking-tight whitespace-nowrap">
                                                    {p.display}
                                                </span>
                                            </span>
                                        </a>
                                    ))}
                                </div>
                                <div className="mt-6 space-y-5">
                                    <div data-testid="contact-address-display" className="flex items-start gap-4">
                                        <MapPin className="w-5 h-5 text-[#E85A1D] mt-0.5 shrink-0" />
                                        <div>
                                            <p className="text-xs font-bold uppercase tracking-wider text-white/50">Address</p>
                                            <p className="text-white font-semibold mt-1">
                                                17 Ames Close, Luton,
                                                <br />
                                                LU3 4AS, United Kingdom
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-4">
                                        <Clock className="w-5 h-5 text-[#E85A1D] mt-0.5 shrink-0" />
                                        <div>
                                            <p className="text-xs font-bold uppercase tracking-wider text-white/50">Service Area</p>
                                            <p className="text-white font-semibold mt-1">Luton & surrounding areas</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Reveal>
                        <Reveal delay={0.18} className="flex-1">
                            <div className="rounded-2xl overflow-hidden border border-white/10 h-72 lg:h-full min-h-[280px]">
                                <iframe
                                    data-testid="google-map-iframe"
                                    title="Appex Smart Solution location — 17 Ames Close, Luton, LU3 4AS"
                                    src="https://www.google.com/maps?q=17+Ames+Close,+Luton+LU3+4AS,+United+Kingdom&output=embed"
                                    className="w-full h-full border-0"
                                    loading="lazy"
                                    allowFullScreen
                                    referrerPolicy="no-referrer-when-downgrade"
                                />
                            </div>
                        </Reveal>
                    </div>
                </div>
            </div>
        </section>
    );
}
